import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import { useState } from "react";

import truncate from "truncate-html";

import xss from "xss";

import includes from "lodash/includes";

import {
  Box,
  Button,
  FiArrowRight,
  Flex,
  HTMLWrapper,
  Text,
  Link,
  HorizontalScrollMenu,
  IconButton,
  FiEdit,
  Contribution,
  VStack,
  Heading
} from "@/storybook-components";
import {
  ehqWhiteListAttributes,
  dateWithSiteLocale,
  buildImgixUrl,
  stripAllHtml
} from "@/lib";
import type { Brainstormer, Idea as IdeaType } from "@/types";
import { useAppSelector } from "@/redux/store/hooks";
import Alerts from "@/components/alerts";
import useUser from "@/hooks/useUser";

import { getRandomColor } from "@/helpers/get-random-color";
import useEmbeds from "@/hooks/useEmbeds";

const Idea = ({
  brainstormer
}: {
  brainstormer: Partial<
    Brainstormer & {
      ideas: { meta: { count: number }; ideaList: Array<Partial<IdeaType>> };
    }
  >;
}) => {
  const router = useRouter();
  const { isEmbed, isInsideIframe, isInsideIframeAndOnCurrentUrl } =
    useEmbeds();
  const { translate, language } = useTranslator();
  const { isRegisteredUserLoggedIn } = useUser();
  const { settings } = useAppSelector((state) => state.settings);
  const { backgroundColours } = useAppSelector(
    (state) => state.toolCustomizations
  );
  const { visibilityMode, canContribute } = useAppSelector(
    (state) => state.project.project
  );

  const { id, ideas, permalink } = brainstormer;
  const { meta, ideaList } = ideas;
  const { projectId } = router.query as { projectId: string };

  const getRouteTarget = () =>
    isEmbed && isInsideIframe && !isInsideIframeAndOnCurrentUrl
      ? "_blank"
      : "_parent";

  const {
    name,
    description,
    state,
    archivalReasonMessage,
    allowCommentsForIdeas
  } = brainstormer;
  const [showArchiveMessage, setShowArchiveMessage] = useState(
    state === "archived"
  );
  const [showDraftMessage, setShowDraftMessage] = useState(state === "draft");
  const [showPanelledVisibilityMessage, setshowPanelledVisibilityMessage] =
    useState(
      (visibilityMode === "Panelled" && !isRegisteredUserLoggedIn) ||
        (visibilityMode === "Panelled" && !canContribute)
    );

  const ideasWithExtraItem = [
    ...ideaList,
    ...(state === "archived" ||
    (visibilityMode === "Panelled" && !isRegisteredUserLoggedIn) ||
    (visibilityMode === "Panelled" && !canContribute)
      ? []
      : [
          // {
          //   id: "joinConversation"
          // }
        ])
  ];

  const joinConversationNode = (key?: string) => {
    return (
      <Flex
        key={key}
        tabIndex={0}
        align="center"
        cursor="pointer"
        justify="center"
        flexDir="column"
        borderRadius="lg"
        background="gray.200"
        height="full"
        width={{
          md: 288,
          sm: meta?.count <= 0 ? "full" : 212,
          base: meta?.count <= 0 ? "full" : 212
        }}
        onClick={() =>
          window.open(
            `/${projectId}/brainstormers/${id}?action=new_idea`,
            getRouteTarget()
          )
        }
        _focusVisible={{
          outline: "none",
          boxShadow: "outline"
        }}
      >
        <Text fontSize="lg" fontWeight={700}>
          {translate("brainstormers.joinConversation")}
        </Text>
        <Text fontWeight={400}>
          {translate("brainstormers.postYourOpinionToday")}
        </Text>
        <IconButton
          mt={4}
          isRound
          tabIndex={0}
          onClick={null}
          icon={<FiEdit />}
          aria-label={translate("brainstormers.createPost")}
        />
      </Flex>
    );
  };

  const colorList = ["#975A16", "#2C7A7B", "#B83280", "#6B46C1"];

  return (
    <Box py={6} mx={{ md: 6, sm: 4, base: 4 }}>
      <Heading as="h1" fontSize="2xl" fontWeight={700}>
        <Link
          target={getRouteTarget()}
          color="brand.500"
          rel="noopener noreferrer"
          href={`/${projectId}/brainstormers/${permalink}`}
        >
          {name}
        </Link>
      </Heading>
      <HTMLWrapper
        my={4}
        html={truncate(
          xss(description, {
            whiteList: {
              p: ehqWhiteListAttributes,
              s: ehqWhiteListAttributes,
              u: ehqWhiteListAttributes,
              em: ehqWhiteListAttributes,
              ul: ehqWhiteListAttributes,
              li: ehqWhiteListAttributes,
              ol: ehqWhiteListAttributes,
              br: ehqWhiteListAttributes,
              strong: ehqWhiteListAttributes
            },
            stripIgnoreTag: true,
            stripIgnoreTagBody: ["table", "header", "img", "video"],
            onTag: (tag) => {
              if (
                includes(
                  [
                    "img",
                    "area",
                    "base",
                    "col",
                    "embed",
                    "hr",
                    "input",
                    "link",
                    "meta",
                    "param",
                    "source",
                    "track",
                    "wbr"
                  ],
                  tag
                )
              )
                return "";
            }
          }),
          { length: 500 }
        )}
      />
      {(showDraftMessage ||
        showArchiveMessage ||
        showPanelledVisibilityMessage) && (
        <VStack>
          {showDraftMessage && (
            <Alerts
              status="info"
              description={translate("brainstormers.thisToolIsInDraft")}
              onClose={() => setShowDraftMessage(false)}
            />
          )}
          {showArchiveMessage && (
            <Alerts
              status="info"
              description={
                archivalReasonMessage ||
                translate("brainstormers.boardArchived")
              }
              onClose={() => setShowArchiveMessage(false)}
            />
          )}
          {showPanelledVisibilityMessage && (
            <Alerts
              status="info"
              description={translate("brainstormers.visibilityPanelled")}
              onClose={() => setshowPanelledVisibilityMessage(false)}
            />
          )}
        </VStack>
      )}
      {meta?.count <= 0 ? null : (
        <>
          <Flex mt={12} justifyContent="space-between">
            <Heading as="h2" fontSize="lg" fontWeight={700}>
              {translate("common.latestPosts")}
            </Heading>
            {meta?.count <= 1 ? null : (
              <Button
                size="xs"
                fontWeight="500"
                variant="tertiary"
                rightIcon={<FiArrowRight />}
                onClick={() =>
                  window.open(
                    `/${projectId}/brainstormers/${permalink}`,
                    getRouteTarget()
                  )
                }
              >
                {translate("common.seeAll")}
              </Button>
            )}
          </Flex>
          <Box my={6}>
            <HorizontalScrollMenu
              itemHeight="full"
              px={{ md: 6, sm: 4, base: 4 }}
              itemWidth={{
                md: 288,
                sm: meta?.count === 1 ? "full" : 212,
                base: meta?.count === 1 ? "full" : 212
              }}
            >
              {ideasWithExtraItem.map((idea: any) => {
                if (idea.id === "joinConversation")
                  return joinConversationNode(idea.id);

                return (
                  <Link
                    key={idea.id}
                    target={getRouteTarget()}
                    display="block"
                    textDecoration="none"
                    rel="noopener noreferrer"
                    sx={{ _hover: { textDecoration: "none" } }}
                    href={`/${projectId}/brainstormers/${permalink}?idea=${idea.id}`}
                  >
                    <Contribution
                      isLoaded
                      showImage
                      isThumbsUp
                      showFooter
                      showDescription
                      showCommentIcon={allowCommentsForIdeas}
                      makeFooterViewOnly
                      imageAlt={idea.name}
                      cardVariant="unstyled"
                      username={idea.author}
                      cardHeading={idea.name}
                      cardDescription={
                        idea.description ? stripAllHtml(idea.description) : ""
                      }
                      thumbsUpCount={idea.votesCount || 0}
                      commentsCount={idea.commentsCount || 0}
                      imageSrc={buildImgixUrl(idea.mediaUrl, 162, 288, "min")}
                      userAvatarSrc={buildImgixUrl(idea.avatar)}
                      isShareIcon={!settings.disableSocialMediaSharing}
                      contributionTime={dateWithSiteLocale(
                        idea.createdAt,
                        language.toLowerCase()
                      )}
                      tileBackgroundColor={
                        idea.backgroundColourId
                          ? backgroundColours.find(
                              ({ id }) =>
                                `${idea.backgroundColourId}` === `${id}`
                            )?.name
                          : getRandomColor(colorList)
                      }
                      ariaLabelShareIcon={translate("common.share")}
                      ariaLabelVoteIcon={`${translate("common.numberOfVotes", {
                        votesCount: idea.votesCount
                      })}`}
                      ariaLabelCommentIcon={`${translate(
                        "common.numberOfComments",
                        { commentsCount: idea.commentsCount }
                      )}`}
                    />
                  </Link>
                );
              })}
            </HorizontalScrollMenu>
          </Box>
        </>
      )}
      <Button
        variant="primary"
        mt={12}
        display="block"
        size={{ base: "lg", md: "md" }}
        width={{ base: "full", md: "auto" }}
        onClick={() =>
          window.open(
            `/${projectId}/brainstormers/${permalink}`,
            getRouteTarget()
          )
        }
      >
        {meta?.count <= 1
          ? translate("brainstormers.haveYourSay")
          : translate("common.seeAllPosts", { count: meta?.count })}
      </Button>
    </Box>
  );
};

export default Idea;
